import {
  Glass,
  Dot,
  ScreenLabel,
  DisplayText,
  BodyText,
  BackNav,
  Flag,
} from "../../components/mobile/primitives";

/** Biomarker index — all extracted biomarkers grouped by category with counts and flags */
export function FlowBiomarkerIndex() {
  const categories = [
    {
      name: "Hematology",
      color: "var(--color-kin-rose)",
      markers: [
        { slug: "wbc", label: "WBC", flag: "HIGH" },
        { slug: "rbc", label: "RBC", flag: null },
        { slug: "hemoglobin", label: "Hemoglobin", flag: null },
        { slug: "hematocrit", label: "Hematocrit", flag: null },
        { slug: "platelets", label: "Platelets", flag: null },
        { slug: "mcv", label: "MCV", flag: null },
      ],
    },
    {
      name: "Metabolic",
      color: "var(--color-kin-orange)",
      markers: [
        { slug: "glucose-fasting", label: "Glucose (fasting)", flag: null },
        { slug: "hba1c", label: "HbA1c", flag: null },
        { slug: "creatinine", label: "Creatinine", flag: null },
        { slug: "bun", label: "BUN", flag: null },
      ],
    },
    {
      name: "Lipids",
      color: "var(--color-kin-sky)",
      markers: [
        { slug: "ldl", label: "LDL Cholesterol", flag: "HIGH" },
        { slug: "hdl", label: "HDL Cholesterol", flag: null },
        { slug: "triglycerides", label: "Triglycerides", flag: null },
        { slug: "total-cholesterol", label: "Total Cholesterol", flag: null },
      ],
    },
    {
      name: "Vitamins",
      color: "var(--color-kin-gold)",
      markers: [
        { slug: "vitamin-d", label: "Vitamin D", flag: "LOW" },
        { slug: "vitamin-b12", label: "Vitamin B12", flag: null },
        { slug: "folate", label: "Folate", flag: null },
      ],
    },
    {
      name: "Thyroid",
      color: "var(--color-kin-lavender)",
      markers: [
        { slug: "tsh", label: "TSH", flag: "HIGH" },
        { slug: "free-t4", label: "Free T4", flag: null },
      ],
    },
  ];

  const totalMarkers = categories.reduce((sum, c) => sum + c.markers.length, 0);
  const flaggedCount = categories.reduce(
    (sum, c) => sum + c.markers.filter((m) => m.flag).length,
    0,
  );

  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Back header */}
      <BackNav label="Vault" />

      {/* Title + stats */}
      <div>
        <DisplayText size={22}>Biomarker Index</DisplayText>
        <BodyText
          size={9.5}
          color="var(--color-kin-muted)"
          weight={400}
          style={{ marginTop: 3, lineHeight: 1.45 }}
        >
          {totalMarkers} markers from 3 sources · {flaggedCount} flagged
        </BodyText>
      </div>

      {/* Summary stats */}
      <Glass style={{ borderRadius: 18, paddingBottom: 4 }}>
        <div style={{ display: "flex", justifyContent: "space-around", padding: "10px 10px 6px" }}>
          {[
            [String(totalMarkers), "Markers"],
            ["3", "Sources"],
            [String(flaggedCount), "Flagged"],
          ].map(([v, l]) => (
            <div
              key={l}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
            >
              <DisplayText
                size={20}
                color={l === "Flagged" ? "var(--color-kin-rose)" : "var(--color-kin-black)"}
              >
                {v}
              </DisplayText>
              <ScreenLabel style={{ letterSpacing: "0.14em" }}>{l}</ScreenLabel>
            </div>
          ))}
        </div>
      </Glass>

      {/* Category sections with marker chips */}
      {categories.map((cat) => (
        <div key={cat.name}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginBottom: 6,
              paddingLeft: 2,
            }}
          >
            <Dot color={cat.color} size={6} />
            <ScreenLabel style={{ color: cat.color }}>{cat.name}</ScreenLabel>
            <BodyText size={8} color="var(--color-kin-muted)" weight={500}>
              · {cat.markers.length}
            </BodyText>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {cat.markers.map((m) => (
              <div
                key={m.slug}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 10px",
                  borderRadius: 12,
                  background:
                    m.slug === "wbc"
                      ? "linear-gradient(135deg, rgba(255,107,107,0.08), rgba(255,107,107,0.03))"
                      : "rgba(254,253,250,0.58)",
                  border:
                    m.slug === "wbc"
                      ? "1px solid rgba(255,107,107,0.25)"
                      : "1px solid rgba(0,0,0,0.05)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <BodyText size={9} weight={m.slug === "wbc" ? 700 : 500}>
                  {m.label}
                </BodyText>
                {m.flag && <Flag type={m.flag} />}
                {m.slug === "wbc" && (
                  <svg
                    width="6"
                    height="10"
                    viewBox="0 0 8 12"
                    fill="none"
                    stroke="var(--color-kin-rose)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1.5 1l5 5-5 5" />
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
